import { createContext, useContext, useEffect, useEffectEvent } from "react";

/** Tells whoever subscribed that the panel they sit in started a drag. */
export interface DragStartChannel {
  subscribe(listener: () => void): () => void;
  emit(): void;
}

export function createDragStartChannel(): DragStartChannel {
  const listeners = new Set<() => void>();
  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    emit() {
      for (const listener of [...listeners]) listener();
    },
  };
}

/**
 * Provided by a panel that moves under the finger or the mouse (DraggablePanel, the dashboards' widget grids).
 * Its value is the subscribe function, so a subscriber's effect depends on one stable function. Kept free of
 * react-draggable so the app-wide tooltip can read it.
 */
export const DragStartContext = createContext<DragStartChannel["subscribe"] | null>(null);

/**
 * Runs `onDragStart` (its latest version) whenever the panel around the caller starts a drag; nothing outside
 * such a panel. A tooltip positioned on what the panel holds closes then, rather than stay where it opened.
 */
export function useDragStart(onDragStart: () => void): void {
  const subscribe = useContext(DragStartContext);
  const notify = useEffectEvent(onDragStart);
  useEffect(() => subscribe?.(() => notify()), [subscribe]);
}
